import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Input,
  Badge,
  Collapse,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { fuelAPI, vehiclesAPI, projectsAPI } from '../../helpers/api';
import { useIsMobile } from '../../helpers/useMediaQuery';
import FuelFormModal from './FuelFormModal';
import FuelDetailModal from './FuelDetailModal';

interface Vehicle {
  id: string;
  plate: string;
  name?: string;
}

interface Project {
  id: string;
  name: string;
  code?: string;
}

interface FuelRecord {
  id: string;
  date: string;
  quantity: number;
  totalCost: number;
  unitPrice?: number;
  fuelType?: string;
  odometer?: number;
  station?: string;
  invoiceNumber?: string;
  notes?: string;
  vehicleId: string;
  projectId?: string;
  siteId?: string;
  filledById?: string;
  vehicle: {
    id: string;
    plate: string;
    name?: string;
    brand?: string;
    model?: string;
  };
  project?: {
    id: string;
    name: string;
    code?: string;
  };
  site?: {
    id: string;
    name: string;
    code?: string;
  };
  filledBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

const FUEL_TYPES = [
  { value: 'DIESEL', label: 'Dizel' },
  { value: 'GASOLINE', label: 'Benzin' },
  { value: 'LPG', label: 'LPG' },
];

const getFuelTypeLabel = (type?: string) => {
  const found = FUEL_TYPES.find((ft) => ft.value === type);
  return found?.label || type || '-';
};

const getFuelTypeStyle = (type?: string) => {
  switch (type) {
    case 'DIESEL':
      return 'bg-dark bg-opacity-10 text-dark';
    case 'GASOLINE':
      return 'bg-success bg-opacity-10 text-success';
    case 'LPG':
      return 'bg-info bg-opacity-10 text-info';
    default:
      return 'bg-secondary bg-opacity-10 text-secondary';
  }
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatCurrency = (value: number) => {
  return value.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Get date range for current month by default
const getDefaultDateRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: endOfMonth.toISOString().split('T')[0],
  };
};

const FuelList: React.FC = () => {
  const isMobile = useIsMobile();
  const [fuelRecords, setFuelRecords] = useState<FuelRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Filter states
  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [fuelTypeFilter, setFuelTypeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Data for filters
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<FuelRecord | null>(null);

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchFuelRecords();
  }, [startDate, endDate, vehicleFilter, fuelTypeFilter, projectFilter, searchFilter]);

  const fetchFilterData = async () => {
    try {
      const [vehiclesRes, projectsRes] = await Promise.all([
        vehiclesAPI.getAll({ isActive: true }),
        projectsAPI.getAll(),
      ]);
      setVehicles(vehiclesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Filter data fetch error:', error);
    }
  };

  const fetchFuelRecords = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (vehicleFilter) params.vehicleId = vehicleFilter;
      if (fuelTypeFilter) params.fuelType = fuelTypeFilter;
      if (projectFilter) params.projectId = projectFilter;

      const response = await fuelAPI.getAll(params);
      let filtered = response.data;
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        filtered = filtered.filter((r: FuelRecord) =>
          r.vehicle.plate.toLowerCase().includes(s) ||
          (r.station && r.station.toLowerCase().includes(s)) ||
          (r.vehicle.brand && r.vehicle.brand.toLowerCase().includes(s)) ||
          (r.vehicle.model && r.vehicle.model.toLowerCase().includes(s)) ||
          (r.invoiceNumber && r.invoiceNumber.toLowerCase().includes(s))
        );
      }
      setFuelRecords(filtered);
    } catch (error) {
      console.error('Fuel records fetch error:', error);
      toast.error('Yakıt kayıtları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = (record: FuelRecord, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedRecord(record);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedRecord(null);
  };

  const clearFilters = () => {
    const defaultDates = getDefaultDateRange();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setVehicleFilter('');
    setFuelTypeFilter('');
    setProjectFilter('');
    setSearchFilter('');
  };

  const hasActiveFilters =
    searchFilter || vehicleFilter || fuelTypeFilter || projectFilter ||
    startDate !== getDefaultDateRange().startDate ||
    endDate !== getDefaultDateRange().endDate;

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Yakıt Kayıtları</h4>
            <div className="page-title-right">
              <Button color="primary" onClick={() => setFormModalOpen(true)}>
                <i className="ri-add-line me-1"></i>
                Yeni Kayıt
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">Yakıt Listesi</h5>
                </Col>
                {/* Desktop Filters */}
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Plaka, istasyon..."
                          style={{ width: '150px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="date"
                          bsSize="sm"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={{ width: '140px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <span className="text-muted">-</span>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="date"
                          bsSize="sm"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={{ width: '140px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={vehicleFilter}
                          onChange={(e) => setVehicleFilter(e.target.value)}
                          style={{ minWidth: '140px' }}
                        >
                          <option value="">Tüm Araçlar</option>
                          {vehicles.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.plate}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={fuelTypeFilter}
                          onChange={(e) => setFuelTypeFilter(e.target.value)}
                          style={{ minWidth: '100px' }}
                        >
                          <option value="">Tüm Türler</option>
                          {FUEL_TYPES.map((ft) => (
                            <option key={ft.value} value={ft.value}>
                              {ft.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                          style={{ minWidth: '140px' }}
                        >
                          <option value="">Tüm Projeler</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            title="Filtreleri Temizle"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && (
                        <Badge color="primary" className="ms-2">
                          {[vehicleFilter, fuelTypeFilter, projectFilter].filter(Boolean).length + (hasActiveFilters ? 1 : 0)}
                        </Badge>
                      )}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Plaka, istasyon..."
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Başlangıç</label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Bitiş</label>
                        <Input
                          type="date"
                          bsSize="sm"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Araç</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={vehicleFilter}
                          onChange={(e) => setVehicleFilter(e.target.value)}
                        >
                          <option value="">Tüm Araçlar</option>
                          {vehicles.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.plate}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Yakıt Türü</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={fuelTypeFilter}
                          onChange={(e) => setFuelTypeFilter(e.target.value)}
                        >
                          <option value="">Tüm Türler</option>
                          {FUEL_TYPES.map((ft) => (
                            <option key={ft.value} value={ft.value}>
                              {ft.label}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Proje</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={projectFilter}
                          onChange={(e) => setProjectFilter(e.target.value)}
                        >
                          <option value="">Tüm Projeler</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </Input>
                      </div>

                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : fuelRecords.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ri-gas-station-line display-4 text-muted"></i>
                  <p className="mt-3 text-muted">Yakıt kaydı bulunamadı</p>
                  <Button color="primary" size="sm" onClick={() => setFormModalOpen(true)}>
                    İlk Kaydı Ekle
                  </Button>
                </div>
              ) : isMobile ? (
                // Mobile: Card View
                <div className="admin-card-list">
                  {fuelRecords.map((record) => (
                    <div key={record.id} className="admin-card-item">
                      {/* Card Header */}
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(record)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div>
                          <h6 className="admin-card-title">{record.vehicle.plate}</h6>
                          <small className="text-muted">{formatDate(record.date)}</small>
                        </div>
                        <span className="text-success fw-semibold">
                          {formatCurrency(record.totalCost)} ₺
                        </span>
                      </div>

                      {/* Card Body */}
                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(record)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-gas-station-line"></i>
                            Miktar
                          </span>
                          <span className="admin-info-value">
                            {record.quantity.toLocaleString('tr-TR')} lt
                          </span>
                        </div>
                        {record.fuelType && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-drop-line"></i>
                              Tür
                            </span>
                            <span className="admin-info-value">
                              <span
                                className={`px-2 py-1 rounded-pill small ${getFuelTypeStyle(record.fuelType)}`}
                                style={{ fontWeight: 500 }}
                              >
                                {getFuelTypeLabel(record.fuelType)}
                              </span>
                            </span>
                          </div>
                        )}
                        {record.station && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-store-2-line"></i>
                              İstasyon
                            </span>
                            <span className="admin-info-value">{record.station}</span>
                          </div>
                        )}
                        {record.project && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-building-line"></i>
                              Proje
                            </span>
                            <span className="admin-info-value">{record.project.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={(e) => openDetailModal(record, e)}
                        >
                          <i className="ri-eye-line"></i>
                          Detaylar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop: Table View
                <div className="table-responsive">
                  <Table className="table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tarih</th>
                        <th>Araç</th>
                        <th>Tür</th>
                        <th>Miktar</th>
                        <th>Tutar</th>
                        <th>Birim Fiyat</th>
                        <th>İstasyon</th>
                        <th>Proje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fuelRecords.map((record) => (
                        <tr
                          key={record.id}
                          onClick={() => openDetailModal(record)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{formatDate(record.date)}</td>
                          <td>
                            <strong>{record.vehicle.plate}</strong>
                            {record.vehicle.name && (
                              <small className="text-muted d-block">{record.vehicle.name}</small>
                            )}
                          </td>
                          <td>
                            {record.fuelType && (
                              <span
                                className={`px-2 py-1 rounded-pill small ${getFuelTypeStyle(record.fuelType)}`}
                                style={{ fontWeight: 500 }}
                              >
                                {getFuelTypeLabel(record.fuelType)}
                              </span>
                            )}
                          </td>
                          <td>{record.quantity.toLocaleString('tr-TR')} lt</td>
                          <td className="text-success fw-semibold">
                            {formatCurrency(record.totalCost)} ₺
                          </td>
                          <td className="text-muted">
                            {record.unitPrice ? `${Number(record.unitPrice).toFixed(2)} ₺/lt` : '-'}
                          </td>
                          <td className="text-muted">{record.station || '-'}</td>
                          <td className="text-muted">
                            {record.project?.name || '-'}
                            {record.site && (
                              <small className="d-block">{record.site.name}</small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni Kayıt Modal */}
      <FuelFormModal
        isOpen={formModalOpen}
        toggle={() => setFormModalOpen(false)}
        onSuccess={() => {
          fetchFuelRecords();
        }}
      />

      {/* Detay Modal */}
      <FuelDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        fuelRecord={selectedRecord}
        onUpdate={() => {
          fetchFuelRecords();
        }}
      />
    </>
  );
};

export default FuelList;
